// src/components/DashboardLayout.tsx
'use client';

import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import ClassicLayout from './layouts/ClassicLayout';
import CleanMacLayout from './layouts/CleanMacLayout';
import FloatingLayout from './layouts/FloatingLayout';
import MinimalistLayout from './layouts/MinimalistLayout';
import BoxedLayout from './layouts/BoxedLayout';

interface UserPayload {
  username?: string;
  role?: string;
  sub?: string;
  avatar_url?: string;
  full_name?: string;
}

export default function DashboardLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const router = useRouter();
  const pathname = usePathname(); // Para tracking de rutas
  
  const [userName, setUserName] = useState('Usuario');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState<string>('U');
  const [layoutTheme, setLayoutTheme] = useState('classic');
  const [isMounted, setIsMounted] = useState(false);
  
  // 🛡️ NUEVO ESTADO DE SEGURIDAD
  const [isAuthorized, setIsAuthorized] = useState(false); 

  const [config, setConfig] = useState<any>(null);

  const API_URL_STATIC = "/api"; 

  useEffect(() => {
    setIsMounted(true);
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      router.replace('/login');
      return;
    }

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload: UserPayload = JSON.parse(decodeURIComponent(atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
      
      const currentRole = (payload.role || 'CAJERO').toUpperCase();
      
      // ==========================================================
      // 🚨 MÓDULO DE SEGURIDAD DE RUTAS (ROUTE GUARD) 🚨
      // Si el rol es CAJERO, le prohibimos ver cualquier Layout de Admin
      // ==========================================================
      if (currentRole === 'CAJERO') {
        router.replace('/cajero/panel');
        return; // Detenemos la ejecución
      }

      // Si llegó hasta aquí, significa que es ADMIN, SUPERVISOR, etc. Lo dejamos pasar.
      setIsAuthorized(true);

      const displayName = payload.full_name || payload.username || 'Usuario';
      setUserName(displayName);
      setUserRole(currentRole);
      
      if (payload.avatar_url) {
        setUserAvatar(payload.avatar_url.startsWith('http') ? payload.avatar_url : `/api${payload.avatar_url}`);
      } else {
        setUserInitials(displayName.charAt(0).toUpperCase());
      }
      
    } catch (error) {
      // Si el token está corrupto o alterado, lo sacamos por seguridad.
      localStorage.removeItem('access_token');
      router.replace('/login');
      return;
    }

    const savedTheme = localStorage.getItem('theme_layout') || 'classic';
    setLayoutTheme(savedTheme);

    fetch('/api/configuracion', { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => {
        if (data && !data.error) setConfig(data);
      })
      .catch((err) => console.error("Error cargando config en layout:", err));

  }, [router, pathname]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    router.push('/login');
  };

  // 🛡️ PANTALLA DE CARGA DE SEGURIDAD
  // Evita el "parpadeo" de la interfaz mientras verificamos si la cajera es intrusa
  if (!isMounted || userRole === null || !isAuthorized) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">Verificando credenciales...</p>
      </div>
    );
  }

  const layoutProps = { children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL: API_URL_STATIC };

  if (layoutTheme === 'cleanmac') return <CleanMacLayout {...layoutProps} />;
  if (layoutTheme === 'floating') return <FloatingLayout {...layoutProps} />;
  if (layoutTheme === 'minimalist') return <MinimalistLayout {...layoutProps} />; 
  if (layoutTheme === 'boxed') return <BoxedLayout {...layoutProps} />;
  
  return <ClassicLayout {...layoutProps} />;
}